import {
    useLoanMultipliersUtil,
    useVaultApyBreakdown,
    useFarmingInfoByActionTypeAndAddress,
    ActionType,
    FarmingInfoExpanded
} from "@bridgesplit/abf-react";
import { formatPercent } from "@bridgesplit/utils";
import { Tooltip, Row, Text, Icon, Column, HoverableText } from "@bridgesplit/ui";
import { Divider } from "@mui/material";

import { RewardsPortfolioBorrowApyProps as RewardsPortfolioApyProps } from "../types";
import { RewardsDailyPointsBreakdown, RewardsDailyCustomPoints } from "../daily-rewards";
import { TooltipContent, useLendVaultAdditionalStats } from "../common";
import { useRewardsColor } from "../util";
import { BaseApy } from "../BaseApy";
import { RewardsProtocolPoints } from "../RewardsProtocolPoints";

export function PortfolioLendVaultApy(props: RewardsPortfolioApyProps) {
    const { loanMultipliers, isLoading } = useLoanMultipliersUtil();
    const { farmingInfo, hasFarmingInfo } = useFarmingInfoByActionTypeAndAddress(
        props.lendingVaultExpanded?.vault.address,
        ActionType.Lend
    );

    const breakdown = useVaultApyBreakdown({ lendingVaultExpanded: props.lendingVaultExpanded, includeRewards: true });

    const color = useRewardsColor(loanMultipliers, props.identifier ? [props.identifier] : undefined, [
        props.loopscalePointsAction
    ]);

    return (
        <Tooltip
            noPadding
            title={<RewardsPortfolioTooltip {...props} farmingInfo={farmingInfo} hasFarmingInfo={hasFarmingInfo} />}
            reverseColors
        >
            <Row spacing={0.5}>
                <Text sx={{ color }} loading={isLoading}>
                    <Icon type="points" />
                </Text>
                <HoverableText variant={props.rateDisplayTextVariant} color={props.rateDisplayColor} underline>
                    {formatPercent(props.totalApy ?? breakdown?.totalApy)}
                </HoverableText>
                {!props.hideApyText && (
                    <Text variant="body1" color="caption">
                        APY
                    </Text>
                )}
            </Row>
        </Tooltip>
    );
}

interface RewardsPortfolioTooltipProps extends RewardsPortfolioApyProps {
    farmingInfo: FarmingInfoExpanded | undefined;
    hasFarmingInfo: boolean;
}

function RewardsPortfolioTooltip(props: RewardsPortfolioTooltipProps) {
    const combinedStats = useLendVaultAdditionalStats(props.lendingVaultExpanded);

    return (
        <TooltipContent px={0} py={1.5} spacing={1}>
            <Column px={1.5} spacing={0.5}>
                <BaseApy variant="body2" {...props} additionalStats={combinedStats} />
            </Column>
            <Divider />
            <Column px={1.5} spacing={0.5}>
                <RewardsDailyCustomPoints
                    loopscalePointsAction={props.loopscalePointsAction}
                    identifier={props.identifier}
                    customDailyPoints={props.customDailyPoints}
                />
                <Column sx={{ paddingLeft: 1.5 }}>
                    <RewardsDailyPointsBreakdown {...props} />
                </Column>
            </Column>

            {props.hasFarmingInfo && (
                <>
                    <Divider />
                    <Column px={1.5}>
                        <RewardsProtocolPoints farmingInfo={props.farmingInfo} />
                    </Column>
                </>
            )}
        </TooltipContent>
    );
}
